import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';
import { Composer, makeRT, resizeRT } from './fx/pass.js';
import { Bloom } from './fx/bloom.js';
import { SSAO } from './fx/ssao.js';
import { TAA } from './fx/taa.js';
import { makeComposite } from './fx/composite.js';
import { buildLUT, LOOKS } from './fx/lut.js';

/**
 * The post chain.
 *
 * The scene is rendered into a HalfFloat target with a sampleable depth
 * attachment — three switches its in-material tonemapping off automatically
 * when the destination is a render target, so what lands there is honest
 * linear HDR. From then on:
 *
 *   scene(HDR+depth) → TAA/AA resolve → SSAO(½) → bloom pyramid → composite
 *
 * Twelve draw calls at `high`, and every stage falls out cleanly when its
 * `CFG.q` flag is off. Nothing in `render()` allocates.
 *
 * Publishes `world.pipeline` and sets `engine.pipeline`.
 */

const _proj = new THREE.Matrix4();
const _jit = new THREE.Vector2();
const _v = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _col = new THREE.Color();

const IMPULSES = {
  hit:    { radial: 0.0055, ca: 0.0016, flash: 0.030, col: 0xfff0d8, exp: 0.05 },
  light:  { radial: 0.0035, ca: 0.0010, flash: 0.018, col: 0xfff0d8, exp: 0.03 },
  heavy:  { radial: 0.0140, ca: 0.0038, flash: 0.090, col: 0xffd9a0, exp: 0.10 },
  crit:   { radial: 0.0180, ca: 0.0052, flash: 0.140, col: 0xffe6b0, exp: 0.14 },
  launch: { radial: 0.0160, ca: 0.0040, flash: 0.070, col: 0xffe0c0, exp: 0.10 },
  guard:  { radial: 0.0040, ca: 0.0044, flash: 0.055, col: 0xbfe0ff, exp: 0.05 },
  musou:  { radial: 0.0340, ca: 0.0075, flash: 0.230, col: 0xffcf80, exp: 0.22 },
  hurt:   { radial: 0.0090, ca: 0.0060, flash: 0.130, col: 0xff4436, exp: 0.06 },
  kill:   { radial: 0.0070, ca: 0.0022, flash: 0.045, col: 0xffe0c8, exp: 0.06 },
  quake:  { radial: 0.0120, ca: 0.0026, flash: 0.020, col: 0xffe8d0, exp: 0.04 },
};

export function createPipeline() {
  let engine = null, world = null, renderer = null, composer = null;
  let sceneRT = null, bloom = null, ssao = null, taa = null, composite = null;
  let W = 2, H = 2, dpr = 1;

  const luts = {};
  let lutCur = 'battle', lutNext = 'battle', lutMix = 0, lutSpeed = 4.0;
  /* `impulse('musou')` raises the musou grade. Nothing ever lowered it, so the
   * first musou of the battle re-graded the WHOLE REST of the game — sat 1.42,
   * violet shadows, gold highs — permanently. Latch the base grade and let the
   * musou one expire on its own. */
  let baseGrade = 'battle', gradeUntil = 0;

  const q = { ssao: !!CFG.q.ssao, bloom: !!CFG.q.bloom, motionBlur: !!CFG.q.motionBlur, aa: CFG.q.aa || 'none' };

  // decaying impulse state
  const fx = { radial: 0, ca: 0, flash: 0, exp: 0, hurt: 0 };
  const base = {
    // `bloom` is the composite's uBloomAmt, and it was 0.36 against the 0.62
    // that composite.js declares for the same uniform -- line 125 overwrites
    // it every frame, so the shader's own default was dead and the halo left
    // the pipeline at 58% strength. Measured on a frozen crit frame (the ONLY
    // difference between the four samples was this number): 0.36 -> 0.62 lifts
    // 8.8% of the frame's dark pixels by more than 8 levels and by up to 156
    // where the burst actually lands, for +2.4 levels of frame mean and one
    // point of the >245 fraction. It is local, which is the whole point of a
    // bloom; the threshold is NOT the lever and lowering it only veils (see
    // the r5 correction in docs/PROGRESS.jsonl).
    exposure: 0.95, bloom: 0.62, ao: 0.92, ca: 0.0006, grain: 0.014,
    motion: 0.55, god: 0.14, sharp: 0.38, vignette: 0.28, cloudShadow: 0.26,
    rim: 0.34, sunCore: 2.6, sunHalo: 0.38,
  };

  const api = {
    enabled: true,
    quality: q,
    base,
    stats: { passes: 0 },

    /** Contract §5 — engine calls this instead of renderer.render(). */
    render(scene, camera, dt, realDt) {
      const rt = realDt || dt || 1 / 60;
      decay(rt);
      // The camera rig writes position/quaternion in lateUpdate; nothing has
      // flushed them to matrixWorld yet, and reprojection needs this frame's.
      camera.updateMatrixWorld();
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
      world?.lighting?.beforeRender?.(scene, camera);

      if (!api.enabled || !sceneRT) {
        renderer.setRenderTarget(null);
        renderer.render(scene, camera);
        return;
      }

      const useTAA = (q.aa === 'taa' || q.aa === 'smaa') && taa.ready;
      _proj.copy(camera.projectionMatrix);

      // Sun position on screen (unjittered) for the light shafts.
      updateSunScreen(camera);
      // Reprojection uses *last* frame's view-projection against this frame's
      // camera, so it has to be built before anything jitters the matrices.
      composite.uniforms.uReproject.value.multiplyMatrices(api._prevVP, camera.matrixWorld);

      if (useTAA) {
        taa.jitterFor(engine.time.frame, W, H, _jit);
        camera.projectionMatrix.elements[8] += _jit.x;
        camera.projectionMatrix.elements[9] += _jit.y;
        camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
      }

      renderer.autoClear = true;
      renderer.setRenderTarget(sceneRT);
      renderer.render(scene, camera);
      renderer.autoClear = false;

      let colorTex = sceneRT.texture;
      if (useTAA) {
        colorTex = taa.render(sceneRT.texture, sceneRT.depthTexture, camera);
      }

      const u = composite.uniforms;
      u.uAOAmt.value = 0;
      if (q.ssao) {
        u.tAO.value = ssao.render(sceneRT.depthTexture, camera);
        u.uAOAmt.value = base.ao;
      }
      if (q.bloom) {
        u.tBloom.value = bloom.render(colorTex, W, H);
        u.uBloomAmt.value = base.bloom;
      } else {
        u.uBloomAmt.value = 0;
      }

      u.tColor.value = colorTex;
      u.tDepth.value = sceneRT.depthTexture;
      u.uProjInv.value.copy(camera.projectionMatrixInverse);
      u.uCamWorld.value.copy(camera.matrixWorld);
      u.uExposure.value = base.exposure * (1 + fx.exp);
      u.uRadial.value = fx.radial;
      u.uCA.value = base.ca + fx.ca;
      u.uFlashAmt.value = fx.flash;
      u.uTime.value = engine.time.real;
      u.uMotion.value = (q.motionBlur && api._hasPrev) ? base.motion : 0.0;
      u.uSharp.value = base.sharp;
      u.uGrain.value = base.grain;

      // grade cross-fade
      if (lutMix < 1 && lutCur !== lutNext) {
        lutMix = Math.min(1, lutMix + rt * lutSpeed);
        if (lutMix >= 1) { lutCur = lutNext; lutMix = 0; u.tLutA.value = luts[lutCur]; }
      }
      u.uLutMix.value = lutCur === lutNext ? 0 : lutMix;

      // hurt vignette
      _tmpCol.setRGB(0.50, 0.045, 0.035);
      _col.setRGB(0.34, 0.33, 0.40).lerp(_tmpCol, fx.hurt);
      u.uVigCol.value.copy(_col);
      u.uVig.value.set(base.vignette + fx.hurt * 0.5, 1.5, W / Math.max(1, H));

      syncSkyUniforms();

      if (useTAA) {
        camera.projectionMatrix.copy(_proj);
        camera.projectionMatrixInverse.copy(_proj).invert();
      }
      composer.run(composite, null);
      renderer.setRenderTarget(null);
      renderer.autoClear = true;

      if (useTAA) taa.capture(camera, _proj);
      api._prevVP.multiplyMatrices(_proj, camera.matrixWorldInverse);
      api._hasPrev = true;
    },

    setSize(w, h, d) {
      dpr = d || 1;
      W = Math.max(2, Math.round(w * dpr));
      H = Math.max(2, Math.round(h * dpr));
      if (!sceneRT) return;
      resizeRT(sceneRT, W, H);
      bloom.setSize(W, H);
      ssao.setSize(W, H);
      taa.setSize(W, H);
      composite.uniforms.uTexel.value.set(1 / W, 1 / H);
      composite.uniforms.uRes.value.set(W, H);
    },

    setExposure(v) { base.exposure = v; },
    setBloom(v) { base.bloom = v; },
    setVignette(v) { base.vignette = v; },
    setSharpen(v) { base.sharp = v; },
    setGodRays(v) { composite.uniforms.uGod.value.x = v; base.god = v; },
    setMotionBlur(v) { base.motion = v; },
    setCloudShadow(v) { base.cloudShadow = v; },
    setRim(v) { base.rim = v; },
    setSunCore(v) { base.sunCore = v; },
    /** 0 off, 1 AO, 2 bloom, 3 depth, 4 velocity — for capture/QA only. */
    debugView(mode) { composite.uniforms.uDebug.value = mode | 0; return mode | 0; },

    /**
     * Bright-pass threshold, in linear HDR. Exposed for the same reason
     * `debugView` is: without it a critic cannot answer "does anything in
     * this scene clear the threshold at all", and the only alternative is a
     * rebuild-and-recapture per value. Reads back so a probe can record what
     * it actually set.
     */
    bloomThreshold(v) {
      if (bloom && v !== undefined) bloom.threshold = +v;
      return bloom ? bloom.threshold : -1;
    },

    /**
     * Bloom SPREAD -- the upsample tent radius. Strength and spread are
     * different knobs and they serve different scenes: a crit wants strength
     * (a hot core throwing light onto dark bodies) and the sun wants a small
     * spread (a tight halo the disc can stand out of). Exposed alongside the
     * threshold so a probe can separate them in one frozen frame.
     */
    bloomRadius(v) {
      if (bloom && v !== undefined) bloom.radius = +v;
      return bloom ? bloom.radius : -1;
    },

    /** Cross-fade to a named colour grade. */
    grade(name) {
      const n = luts[name] ? name : 'battle';
      if (n !== 'musou') { baseGrade = n; gradeUntil = 0; }
      if (n === lutNext) return;
      // if a fade is already running, snap it so the new one starts clean
      if (lutCur !== lutNext) { lutCur = lutNext; composite.uniforms.tLutA.value = luts[lutCur]; }
      lutNext = n; lutMix = 0;
      composite.uniforms.tLutB.value = luts[lutNext];
    },

    /** Full-screen additive flash. `amount` decays over ~0.25 s. */
    flash(color, amount = 0.4) {
      if (color !== undefined && color !== null) composite.uniforms.uFlashCol.value.set(color);
      fx.flash = Math.max(fx.flash, amount);
    },

    radialBlur(amount = 0.02) { fx.radial = Math.max(fx.radial, amount); },

    /** One call combat can make on every hit to punch the image. */
    impulse(kind, amount = 1) {
      const p = IMPULSES[kind] || IMPULSES.hit;
      const a = Math.max(0, amount);
      fx.radial = Math.max(fx.radial, p.radial * a);
      fx.ca = Math.max(fx.ca, p.ca * a);
      fx.flash = Math.max(fx.flash, p.flash * a);
      fx.exp = Math.max(fx.exp, p.exp * a);
      composite.uniforms.uFlashCol.value.setHex(p.col).convertSRGBToLinear();
      if (kind === 'hurt') fx.hurt = Math.min(1, fx.hurt + 0.55 * a);
      if (kind === 'musou') { api.grade('musou'); gradeUntil = (world ? world.time.real : 0) + 3.2; }
      return api;
    },

    /** Re-read CFG.q (quality switch at runtime). */
    applyQuality() {
      q.ssao = !!CFG.q.ssao; q.bloom = !!CFG.q.bloom;
      q.motionBlur = !!CFG.q.motionBlur; q.aa = CFG.q.aa || 'none';
      taa?.invalidate();
    },

    _prevVP: new THREE.Matrix4(),
    _hasPrev: false,
  };

  const _tmpCol = new THREE.Color();

  function decay(dt) {
    if (gradeUntil > 0 && (world ? world.time.real : 0) >= gradeUntil) { gradeUntil = 0; api.grade(baseGrade); }
    const k = Math.exp(-dt * 9.0);
    fx.radial *= k; fx.ca *= Math.exp(-dt * 8.0);
    fx.flash *= Math.exp(-dt * 11.0);
    fx.exp *= Math.exp(-dt * 7.0);
    fx.hurt *= Math.exp(-dt * 1.6);
    if (fx.radial < 1e-5) fx.radial = 0;
    if (fx.ca < 1e-6) fx.ca = 0;
    if (fx.flash < 1e-4) fx.flash = 0;
    if (fx.exp < 1e-4) fx.exp = 0;
    if (fx.hurt < 1e-3) fx.hurt = 0;
  }

  function updateSunScreen(camera) {
    const sky = world?.sky;
    const u = composite.uniforms;
    if (!sky) { u.uSunUv.value.z = 0; u.uRim.value.z = 0; u.uSunCore.value.w = 0; return; }
    camera.getWorldDirection(_fwd);
    const facing = _fwd.dot(sky.sunDir);
    // The back-light term wants the sun *behind* the subject, which is the
    // same thing as the camera pointing anywhere towards it — it fades in well
    // before the sun is actually in frame.
    const gate = Math.max(0, Math.min(1, (facing + 0.18) / 0.72));
    u.uRim.value.x = base.rim;
    u.uRim.value.z = gate * (sky.sunDir.y > 0.02 ? 1 : 0);
    _col.copy(sky.sunColor);
    const mx = Math.max(_col.r, Math.max(_col.g, _col.b)) || 1;
    u.uRimCol.value.copy(_col).multiplyScalar(1 / mx);

    if (facing <= 0.02) { u.uSunUv.value.z = 0; u.uSunCore.value.w = 0; return; }
    _v.copy(sky.sunDir).multiplyScalar(2000).add(camera.position).project(camera);
    const sx = _v.x * 0.5 + 0.5, sy = _v.y * 0.5 + 0.5;
    u.uSunUv.value.set(sx, sy, Math.min(1, facing * 1.6) * base.god);

    // Solar core: only when the disc is genuinely on screen and above the
    // horizon. Occlusion is left to the depth-gated god-ray march plus the
    // core's own tiny radius, which a ridge in front of it will cover.
    const on = sx > -0.06 && sx < 1.06 && sy > -0.06 && sy < 1.06 && sky.sunDir.y > 0.01;
    const c = u.uSunCore.value;
    c.x = base.sunCore;
    c.y = 0.020;
    c.z = base.sunHalo;
    c.w = on ? Math.min(1, facing * 2.2) * Math.min(1, sky.sunDir.y * 14) : 0;
  }

  function syncSkyUniforms() {
    const sky = world?.sky;
    if (!sky) return;
    const u = composite.uniforms;
    u.uSunDirW.value.copy(sky.sunDir);
    // Aerial perspective must *lighten* distance or it reads as dirt, so the
    // horizon tint is pushed above the scene's own mid-tone and pulled towards
    // the zenith blue; only the sun side stays warm.
    //
    // `sky.horizonColor` is sampled dead opposite the sun, and at golden hour
    // that direction is the darkest part of the whole dome. Used raw it makes
    // the haze on the anti-sun side too dim to lift anything, which is why a
    // frame turned away from the sun lost its depth ramp entirely while the
    // into-the-sun frame kept one. A fixed share of the sun-side horizon is
    // folded back in so the atmosphere has luminance in every direction.
    u.uFogHorizon.value.copy(sky.horizonColor)
      .lerp(sky.zenithColor, 0.24)
      .lerp(sky.sunHorizon, 0.20)
      .multiplyScalar(1.34);
    u.uFogZenith.value.copy(sky.zenithColor).multiplyScalar(1.16);
    _col.copy(sky.sunHorizon).sub(sky.horizonColor);
    _col.r = Math.max(0, _col.r); _col.g = Math.max(0, _col.g); _col.b = Math.max(0, _col.b);
    u.uFogSun.value.copy(_col).multiplyScalar(0.85);
    u.uGodTint.value.copy(sky.sunColor).multiplyScalar(1.4).addScalar(0.06);
    // cloud shadows track the same sheet + drift the dome is using
    const su = sky.uniforms;
    if (su) {
      u.tCloud.value = su.uClouds.value;
      u.uCloudCov.value.x = su.uCloud.value.x;
      u.uCloudSh.value.x = base.cloudShadow * Math.max(0, Math.min(1, sky.sunDir.y * 3.2));
      u.uCloudSh.value.z = su.uCloud.value.w * 1.9;
      u.uCloudSh.value.w = su.uCloud.value.w * 0.80;
    }
    const m = world?.lighting?._m;
    if (m) {
      // x density, y height falloff (1/m), z height reference, w max blend.
      // fogH is a *height falloff*, not a blend ceiling — mixing the two is
      // what switched aerial perspective off inside the playable range.
      const f = u.uFog.value;
      f.x = m.fog; f.y = m.fogH; f.z = m.fogRef; f.w = m.fogMax;
      u.uFogDesat.value = m.fogDesat;
    }
  }

  return {
    name: 'pipeline', order: ORDER.RENDER,
    init(w) {
      world = w; engine = w.engine; renderer = engine.renderer;
      composer = new Composer(renderer);

      const size = engine.size;
      dpr = size.dpr || 1;
      W = Math.max(2, Math.round(size.w * dpr));
      H = Math.max(2, Math.round(size.h * dpr));

      sceneRT = makeRT(W, H, { depth: true, name: 'sceneHDR' });
      const levels = Math.max(3, Math.min(5, Math.floor(Math.log2(Math.min(W, H))) - 4));
      bloom = new Bloom(composer, levels);
      ssao = new SSAO(composer);
      taa = new TAA(composer, 8);
      composite = makeComposite();

      bloom.setSize(W, H); ssao.setSize(W, H); taa.setSize(W, H);
      composite.uniforms.uTexel.value.set(1 / W, 1 / H);
      composite.uniforms.uRes.value.set(W, H);

      for (const k in LOOKS) luts[k] = buildLUT(k);
      lutCur = lutNext = 'battle';
      composite.uniforms.tLutA.value = luts.battle;
      composite.uniforms.tLutB.value = luts.battle;

      // Renderer-level tonemapping is now ours; the scene target must stay linear.
      renderer.toneMappingExposure = 1.0;

      engine.pipeline = api;
      w.pipeline = api;

      // lighting initialised before us, so its mood never reached the grade
      const m = w.lighting?._m;
      if (m) { api.grade(m.grade); lutCur = lutNext = luts[m.grade] ? m.grade : 'battle';
               composite.uniforms.tLutA.value = composite.uniforms.tLutB.value = luts[lutCur];
               base.exposure = m.exposure; }

      w.bus?.on?.('quality', () => api.applyQuality());

      // Automation surface: __DW.scenario('post', false) etc. Registered here
      // so capture scripts can drive the whole render stack without reaching
      // into module internals.
      const dw = typeof window !== 'undefined' ? window.__DW : null;
      if (dw && dw.scenarios) {
        dw.scenarios.post = (wd, on = true) => { wd.pipeline.enabled = on !== false; return wd.pipeline.enabled; };
        dw.scenarios.grade = (wd, name) => { wd.pipeline.grade(name); return name; };
        dw.scenarios.impulse = (wd, kind, amt = 1) => { wd.pipeline.impulse(kind, amt); return kind; };
        dw.scenarios.debugView = (wd, mode = 0) => wd.pipeline.debugView(mode);
        dw.scenarios.bloomThreshold = (wd, v) => wd.pipeline.bloomThreshold(v);
        dw.scenarios.mood = (wd, name) => { wd.lighting.setMood(name); return name; };
        dw.scenarios.timeOfDay = (wd, t) => { wd.sky.setTimeOfDay(t); wd.lighting.syncFromSky(wd, true); return t; };
        dw.scenarios.rim = (wd, v = 0.62) => { wd.pipeline.setRim(v); return v; };
        dw.scenarios.fog = (wd, v) => {
          const m = wd.lighting?._m; if (m && v !== undefined) m.fog = v;
          return m ? m.fog : 0;
        };
      }
    },

    resize(w, h, d) { api.setSize(w, h, d); },

    dispose() {
      sceneRT?.dispose(); bloom?.dispose(); ssao?.dispose(); taa?.dispose(); composite?.dispose();
      for (const k in luts) luts[k].dispose();
    },
  };
}
